import * as React from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CardMedia,
} from "@mui/material";
import WebIcon from "@mui/icons-material/Web";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import JsonDataModal from "../components/admin/JsonDataModal";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";

export default function SquarespaceWebsiteDetail() {
  const { id } = useParams();
  const api = useApi();
  const [website, setWebsite] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [jsonModalOpen, setJsonModalOpen] = React.useState(false);

  React.useEffect(() => {
    const fetchWebsite = async () => {
      setLoading(true);
      setError(null);
      try {
        const responseData = await api.get(`/admin/squarespace/websites/${id}`);
        const websiteData = responseData.data || responseData;
        setWebsite(websiteData);
      } catch (err) {
        console.error("Error fetching website:", err);
        setError(err.message || "Failed to fetch website");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchWebsite();
    }
  }, [id, api]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Squarespace
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace/websites"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Websites
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  {website?.title || website?.id || website?.websiteId || id}
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Website Details"
              description="View Squarespace website information"
              icon={<WebIcon sx={{ color: "primary.main" }} />}
            >
              {website && (
                <Button
                  variant="outlined"
                  onClick={() => setJsonModalOpen(true)}
                >
                  View JSON
                </Button>
              )}
            </SquarespacePageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : website ? (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
                    Website Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Website ID
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }} fontFamily="monospace">
                        {website.id || website.websiteId || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Title
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {website.title || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <Chip
                          label={website.status || "N/A"}
                          color={
                            website.status === "active" || website.status === "ACTIVE" ? "success" :
                            website.status === "pending" || website.status === "PENDING" ? "warning" :
                            website.status === "suspended" || website.status === "SUSPENDED" ? "error" : "default"
                          }
                          size="small"
                        />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Domain
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {website.domain || website.domainName || website.url || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Plan
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {website.plan || website.planName || "N/A"}
                      </Typography>
                    </Grid>

                    {website.customerId && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Customer ID
                        </Typography>
                        <Box sx={{ mt: 0.25 }}>
                          <Link
                            to={`/dashboard/squarespace/customers/${website.customerId}`}
                            style={{ textDecoration: "none" }}
                          >
                            <Typography
                              variant="body2"
                              color="primary"
                              fontFamily="monospace"
                              sx={{ "&:hover": { textDecoration: "underline" } }}
                            >
                              {website.customerId}
                            </Typography>
                          </Link>
                        </Box>
                      </Grid>
                    )}

                    {website.customerEmail && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Customer Email
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {website.customerEmail}
                        </Typography>
                      </Grid>
                    )}

                    {website.orderId && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Order ID
                        </Typography>
                        <Box sx={{ mt: 0.25 }}>
                          <Link
                            to={`/dashboard/squarespace/orders/${website.orderId}`}
                            style={{ textDecoration: "none" }}
                          >
                            <Typography
                              variant="body2"
                              color="primary"
                              fontFamily="monospace"
                              sx={{ "&:hover": { textDecoration: "underline" } }}
                            >
                              {website.orderId}
                            </Typography>
                          </Link>
                        </Box>
                      </Grid>
                    )}

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Created
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {formatDate(website.createdAt || website.created_at || website.createdOn)}
                      </Typography>
                    </Grid>

                    {(website.zHotUrl || website.zHot_url || website.z_hot_url || website.zHotURL) && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          ZHot URL
                        </Typography>
                        <Box sx={{ mt: 0.25 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            href={website.zHotUrl || website.zHot_url || website.z_hot_url || website.zHotURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textTransform: "none" }}
                          >
                            Open ZHot URL
                          </Button>
                        </Box>
                      </Grid>
                    )}

                    {website._source && (
                      <Grid size={{ xs: 12 }}>
                        <Alert severity="info" sx={{ mt: 2 }}>
                          This website data was derived from order information (websites endpoint not available in Squarespace API)
                        </Alert>
                      </Grid>
                    )}

                    {(website.screenshotUrl || website.screenshot_url || website.screenshot) && (
                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" sx={{ mb: 1 }}>
                            Screenshot
                          </Typography>
                          <Card variant="outlined" sx={{ maxWidth: 800 }}>
                            <CardMedia
                              component="img"
                              image={website.screenshotUrl || website.screenshot_url || website.screenshot}
                              alt="Website screenshot"
                              sx={{
                                width: "100%",
                                height: "auto",
                                maxHeight: 600,
                                objectFit: "contain",
                              }}
                            />
                          </Card>
                        </Box>
                      </Grid>
                    )}

                    {/* Display any additional fields */}
                    {Object.entries(website).map(([key, value]) => {
                      // Skip already displayed fields
                      const displayedFields = [
                        'id', 'websiteId', 'title', 'status', 'domain', 'domainName', 'url',
                        'plan', 'planName', 'customerId', 'customerEmail', 'orderId',
                        'createdAt', 'created_at', 'createdOn', '_source', '_orderData', '_derivedFromOrders',
                        'zHotUrl', 'zHot_url', 'z_hot_url', 'zHotURL',
                        'screenshotUrl', 'screenshot_url', 'screenshot'
                      ];
                      if (displayedFields.includes(key)) return null;
                      if (value === null || value === undefined) return null;
                      
                      return (
                        <Grid key={key} size={{ xs: 12, sm: 6, md: 4 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                          </Typography>
                        </Grid>
                      );
                    })}

                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                          Full Website Data
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            p: 2,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            overflow: "auto",
                            fontSize: "0.875rem",
                            fontFamily: "monospace",
                          }}
                        >
                          {JSON.stringify(website, null, 2)}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ) : (
              <Alert severity="warning">
                Website not found
              </Alert>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
      
      <JsonDataModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        data={website}
        title={`Website JSON - ${website?.title || website?.id || website?.websiteId || "Website"}`}
      />
    </>
  );
}
